import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';

export interface Logger {
  debug: (message: string, meta?: unknown) => void;
  info: (message: string, meta?: unknown) => void;
  warn: (message: string, meta?: unknown) => void;
  error: (message: string, meta?: unknown) => void;
}

export interface HttpClientDeps {
  baseURL: string;
  userAgent: string;
  appId: string;
  /** NATIVE binary marketing version (frozen at store submission), e.g. "1.5.0". */
  appVersion: string;
  /** NATIVE binary build number (iOS CFBundleVersion / Android versionCode). */
  appBuild: string;
  osName: string;
  osVersion: string;
  /**
   * Identity of the JS bundle actually running (expo-updates updateId, or an
   * "embedded:*" marker). Diverges from appVersion once an OTA update applies.
   * Omit on web/embed — they ship no out-of-band bundle.
   */
  bundleId?: string;
  /** Native↔JS compatibility key (expo-updates runtimeVersion, fingerprint policy). */
  runtimeVersion?: string;
  /** OTA release channel this binary follows, e.g. "development" / "production". */
  updateChannel?: string;
  /** Returns the current auth token (or null if unauthenticated). May be sync or async. */
  getAuthToken: () => Promise<string | null> | string | null;
  /** Attempts a silent refresh; returns new token or null. */
  tryRefreshAuthToken: () => Promise<string | null>;
  /** Currently selected i18n locale (BCP-47), e.g. "en-US". */
  getSelectedLanguage: () => string;
  logger: Logger;
  authedTimeoutMs?: number;
  unauthedTimeoutMs?: number;
  /** Extra headers to merge on every request from both clients. */
  extraHeaders?: Record<string, string>;
}

export interface HttpClients {
  httpClient: AxiosInstance;
  unauthClient: AxiosInstance;
}

interface RequestMeta {
  startAt: number;
  interceptorStart: number;
  authDurationMs: number;
}

type ConfigWithMeta = InternalAxiosRequestConfig & {
  __meta?: RequestMeta;
  __isRetryRequest?: boolean;
};

/**
 * Builds an authenticated axios client (with JWT injection + 401 refresh-and-retry)
 * and a parallel unauthenticated client. Caller injects platform-specific deps
 * (auth source, language source, logger).
 */
export function createHttpClient(deps: HttpClientDeps): HttpClients {
  const {
    baseURL,
    userAgent,
    appId,
    appVersion,
    appBuild,
    osName,
    osVersion,
    bundleId,
    runtimeVersion,
    updateChannel,
    getAuthToken,
    tryRefreshAuthToken,
    getSelectedLanguage,
    logger,
    authedTimeoutMs = 8000,
    unauthedTimeoutMs = 5000,
    extraHeaders = {},
  } = deps;

  // Per the Fetch spec, browsers forbid JS from setting certain headers
  // (User-Agent, Accept-Encoding, Host, Connection, …). Axios/XHR will log
  // "Refused to set unsafe header" warnings and Caddy's CORS allow-list
  // doesn't include them either, so the preflight rejects valid requests
  // on iOS Safari. RN's networking stack permits both, so we only strip
  // these when running in a real browser.
  const isBrowser =
    typeof window !== 'undefined' && typeof document !== 'undefined';

  const COMMON_HEADERS: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-App-Id': appId,
    'X-App-Version': appVersion,
    'X-App-Build': appBuild,
    'X-OS-Name': osName,
    'X-OS-Version': osVersion,
    ...extraHeaders,
  };
  // OTA / bundle identity. Only emitted by clients that ship JS out-of-band
  // from the native binary (RN via self-hosted expo-updates). X-App-Version /
  // X-App-Build now describe the NATIVE binary; these describe the running JS
  // bundle and its native-compat fingerprint, letting the backend tell apart
  // "old binary, fresh OTA bundle" from "old binary, stale bundle". Web/embed
  // leave these undefined, so they never enter CORS preflight there.
  if (bundleId) COMMON_HEADERS['X-Bundle-Id'] = bundleId;
  if (runtimeVersion) COMMON_HEADERS['X-Runtime-Version'] = runtimeVersion;
  if (updateChannel) COMMON_HEADERS['X-Update-Channel'] = updateChannel;
  if (!isBrowser) {
    COMMON_HEADERS['User-Agent'] = userAgent;
    COMMON_HEADERS['Accept-Encoding'] = 'gzip, deflate, br';
  }

  const httpClient = axios.create({
    baseURL,
    timeout: authedTimeoutMs,
    headers: { ...COMMON_HEADERS },
  });

  httpClient.interceptors.request.use(async config => {
    const c = config as ConfigWithMeta;
    const interceptorStart = Date.now();
    const authStart = Date.now();
    const token = await getAuthToken();
    const authDurationMs = Date.now() - authStart;

    c.__meta = { startAt: Date.now(), interceptorStart, authDurationMs };

    c.headers['Accept-Language'] = getSelectedLanguage();

    if (authDurationMs > 100) {
      logger.warn('[HTTP] Slow auth', {
        durationMs: authDurationMs,
        url: c.url,
      });
    }

    logger.debug('[HTTP] Request prepared', {
      hasToken: !!token,
      url: c.url,
      method: c.method,
      authDurationMs,
    });

    if (token) {
      c.headers.Authorization = `Bearer ${token}`;
    } else {
      delete c.headers.Authorization;
    }
    c.headers['x-timestamp'] = new Date().toISOString();
    return c;
  });

  httpClient.interceptors.response.use(
    response => {
      try {
        const meta = (response.config as ConfigWithMeta)?.__meta;
        const networkMs = meta ? Date.now() - meta.startAt : undefined;
        const totalMs = meta ? Date.now() - meta.interceptorStart : undefined;
        logger.debug('[HTTP] Response', {
          url: response.config?.url,
          method: response.config?.method,
          status: response.status,
          networkMs,
          authMs: meta?.authDurationMs,
          totalMs,
        });
      } catch {
        // logging is best-effort
      }
      return response;
    },
    async error => {
      const originalRequest = (error.config ?? {}) as ConfigWithMeta;
      const status = error?.response?.status;
      const code = error?.code;

      try {
        const meta = originalRequest.__meta;
        const networkMs = meta ? Date.now() - meta.startAt : undefined;
        const totalMs = meta ? Date.now() - meta.interceptorStart : undefined;
        logger.warn('[HTTP] Response error', {
          status,
          url: originalRequest.url,
          method: originalRequest.method,
          code,
          networkMs,
          authMs: meta?.authDurationMs,
          totalMs,
        });
      } catch {
        // best-effort
      }

      if (
        (status === 401 || status === 403) &&
        !originalRequest.__isRetryRequest
      ) {
        try {
          const newToken = await tryRefreshAuthToken();
          if (newToken) {
            originalRequest.__isRetryRequest = true;
            originalRequest.headers =
              originalRequest.headers ??
              ({} as InternalAxiosRequestConfig['headers']);
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return httpClient(originalRequest);
          }
        } catch {
          // fall through to reject
        }
      }
      return Promise.reject(error);
    },
  );

  const unauthClient = axios.create({
    baseURL,
    timeout: unauthedTimeoutMs,
    headers: { ...COMMON_HEADERS },
  });

  unauthClient.interceptors.request.use(config => {
    config.headers['Accept-Language'] = getSelectedLanguage();
    config.headers['x-timestamp'] = new Date().toISOString();
    return config;
  });

  return { httpClient, unauthClient };
}

export type { AxiosInstance } from 'axios';
